import { concat, fn, get } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import {
  DISTRIBUTION_METHOD_TRANSLATIONS,
  DISTRIBUTION_METHODS,
  type DistributionMethod,
} from 'lumen/utils/constants/budget-forecast-constants';

import styles from './button-distribute.module.css';

interface SettingsBudgetForecastButtonDistributeSignature {
  Args: {
    disableLastYearFinals: boolean;
    distributionMethod: DistributionMethod;
    isDisabled: boolean;
    isLoading: boolean;
    onDistribute: (method: DistributionMethod) => void;
  };
}

export default class SettingsBudgetForecastButtonDistribute extends Component<SettingsBudgetForecastButtonDistributeSignature> {
  @service declare intl: Services['intl'];

  get distributeOptions(): {
    disabled: boolean;
    disabledMessage: string;
    id: DistributionMethod;
    label: string;
  }[] {
    return Object.values(DISTRIBUTION_METHODS).map((method) => ({
      id: method,
      label: this.intl.t(DISTRIBUTION_METHOD_TRANSLATIONS[method]),
      disabled:
        this.args.disableLastYearFinals &&
        method === DISTRIBUTION_METHODS.LAST_YEAR_FINALS,
      disabledMessage: this.intl.t(
        'budget_forecast.distribute.ly_finals.disabled',
      ),
    }));
  }

  get distributionMethod(): DistributionMethod {
    if (this.args.disableLastYearFinals) {
      return DISTRIBUTION_METHODS.EQUAL;
    }

    return this.args.distributionMethod;
  }

  @action
  selectDistributeOption(
    option: (typeof this.distributeOptions)[number],
  ): void {
    this.args.onDistribute(option.id);
  }

  <template>
    <div
      class={{concat
        styles.container
        " "
        "ic-product-tour-49285022-button-distribute"
      }}
      data-test-id="button-distribute"
    >
      <PrismButton
        @isDisabled={{@isDisabled}}
        @isLoading={{@isLoading}}
        @label={{t
          (get DISTRIBUTION_METHOD_TRANSLATIONS this.distributionMethod)
        }}
        @onClick={{fn @onDistribute this.distributionMethod}}
        class={{styles.button}}
        data-test-id="button-distribute-main-action"
      />

      <PrismComboSelectInput
        @disabled={{@isDisabled}}
        @onChange={{this.selectDistributeOption}}
        @options={{this.distributeOptions}}
        @unsafe_triggerComponent={{component
          GeneralDropdownsTriggersTriggerDropdownIconButton
          class=styles.dropdown
          dataTestId="button-distribute-dropdown-trigger"
          icon="chevron-down"
          isDisabled=@isDisabled
        }}
        @value={{null}}
        data-test-id="button-distribute-dropdown"
      />
    </div>
  </template>
}
